import { inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { injectQuery, injectMutation, QueryClient } from '@tanstack/angular-query-experimental';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ApiResponse,
  BanksData,
  CreateBankRequest,
  CreateBankData,
  DeleteBankRequest,
  DeleteBankData,
  ParsedBank,
  Bank,
  FaucetInfoResponse,
} from '../models';

function parseBankInfo(bank: Bank): ParsedBank {
  const [name, country, currency, code] = bank.info.split(':');
  return {
    id: bank.id,
    pubkey: bank.pubkey,
    name: name || '',
    country: country || '',
    currency: currency || '',
    code: code || '',
  };
}

// Query
export function injectBanksQuery() {
  const http = inject(HttpClient);

  return injectQuery(() => ({
    queryKey: ['banks'],
    queryFn: () =>
      firstValueFrom(
        http.get<ApiResponse<BanksData>>('/banks').pipe(
          map((response) => response.data.banks.map(parseBankInfo))
        )
      ),
  }));
}

export function injectFaucetInfoQuery() {
  const http = inject(HttpClient);

  return injectQuery(() => ({
    queryKey: ['faucetInfo'],
    queryFn: () =>
      firstValueFrom(
        http.get<ApiResponse<FaucetInfoResponse>>('/faucet/info').pipe(
          map((response) => response.data)
        )
      ),
  }));
}

// Mutation
export function injectCreateBankMutation() {
  const http = inject(HttpClient);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: (data: CreateBankRequest) =>
      firstValueFrom(
        http.post<ApiResponse<CreateBankData>>('/admin/banks', data).pipe(
          map((response) => response.data)
        )
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banks'] });
    },
  }));
}

export function injectDeleteBankMutation() {
  const http = inject(HttpClient);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: (data: DeleteBankRequest) =>
      firstValueFrom(
        http.delete<ApiResponse<DeleteBankData>>('/admin/banks', { body: data }).pipe(
          map((response) => response.data)
        )
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banks'] });
    },
  }));
}
